import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { toast } from 'react-toastify';
import { getCompletedChallenges, getCompletedChallengesSummary } from '../../../../services/dashboard.service';
import { ChallengePageInfo, ChallengeRewardSummary, CreatorChallenge } from '../../../../types/challenges';
import { formatChallengeAmount } from '../../../../utils/challenge-utils';
import useCursorChallenges from '../../../../hooks/useCursorChallenges';
import useIsMobile from '../../../../hooks/useIsMobile';
import ThreeDotsLoader from '../../../common/three-dots-loader/three-dots-loader';
import PaginationPrevIcon from '../../../../assets/icons/challenge-pagination-prev.svg';
import PaginationNextIcon from '../../../../assets/icons/challenge-pagination-next.svg';
import CompletedChallengeCard from './CompletedChallengeCard';
import CompletedChallengesTable from './CompletedChallengesTable';
import RewardDetailsDrawer from './RewardDetailsDrawer';
import { ChallengesRefetchHandle } from './ActiveChallengesList';
import styles from './allChallenges.module.css';

type CompletedChallengesProps = {
  defaultCurrency: string;
  onClaimReward: (challenge: CreatorChallenge) => void;
};

const CompletedChallenges = forwardRef<ChallengesRefetchHandle, CompletedChallengesProps>(
  ({ defaultCurrency, onClaimReward }, ref) => {
    const isMobile = useIsMobile();
    const [summary, setSummary] = useState<ChallengeRewardSummary | null>(null);
    const [pageChallenges, setPageChallenges] = useState<CreatorChallenge[]>([]);
    const [pageInfo, setPageInfo] = useState<ChallengePageInfo | null>(null);
    const [isPageLoading, setIsPageLoading] = useState<boolean>(!isMobile);
    const [drawerChallenge, setDrawerChallenge] = useState<CreatorChallenge | null>(null);

    const {
      challenges: infiniteChallenges,
      isLoading: isInfiniteLoading,
      isLoadingMore,
      listRef,
      handleScroll,
      refetch: refetchInfinite,
    } = useCursorChallenges((after) => getCompletedChallenges({ after }), isMobile);

    const fetchSummary = async () => {
      const response = await getCompletedChallengesSummary();
      if (response?.status) {
        setSummary(response.data);
      } else {
        toast.error(response?.message || 'Something went wrong');
      }
    };

    const fetchPage = async (cursors: { after?: string | null; before?: string | null } = {}) => {
      setIsPageLoading(true);
      const response = await getCompletedChallenges(cursors);
      if (response?.status) {
        setPageChallenges(response.data.edges.map(({ node }) => node));
        setPageInfo(response.data.pageInfo);
      } else {
        toast.error(response?.message || 'Something went wrong');
      }
      setIsPageLoading(false);
    };

    const refetch = () => {
      fetchSummary();
      if (isMobile) refetchInfinite();
      else fetchPage();
    };

    useImperativeHandle(ref, () => ({ refetch }));

    useEffect(() => {
      fetchSummary();
      if (!isMobile) fetchPage();
    }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

    const challenges = isMobile ? infiniteChallenges : pageChallenges;
    const isLoading = isMobile ? isInfiniteLoading : isPageLoading;
    const isEmpty = !isLoading && challenges.length === 0;
    const showPagination = Boolean(pageInfo?.hasNextPage || pageInfo?.hasPreviousPage);

    if (isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <ThreeDotsLoader />
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>🏆</div>
          <p>No trophies yet! Get assigned a challenge or conquer one.</p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.tiles}>
          <div className={styles.tile}>
            <p className={styles.tileLabel}>Total bonus</p>
            <p className={styles.tileValue}>{formatChallengeAmount(summary?.totalBonusCents || 0, defaultCurrency)}</p>
          </div>
          <div className={styles.tile}>
            <p className={styles.tileLabel}>Products won</p>
            <p className={styles.tileValue}>{summary?.productsWon ?? 0}</p>
          </div>
        </div>

        {isMobile ? (
          <div className={styles.cards} ref={listRef} onScroll={handleScroll}>
            {challenges.map((challenge) => (
              <CompletedChallengeCard
                key={challenge.id}
                challenge={challenge}
                defaultCurrency={defaultCurrency}
                onClaimReward={onClaimReward}
                onOpenDetails={setDrawerChallenge}
              />
            ))}
            {isLoadingMore && (
              <div className={styles.loadMoreContainer}>
                <ThreeDotsLoader />
              </div>
            )}
          </div>
        ) : (
          <>
            <CompletedChallengesTable
              challenges={challenges}
              defaultCurrency={defaultCurrency}
              onClaimReward={onClaimReward}
            />
            {showPagination && (
              <div className={styles.paginationRow}>
                <div className={styles.pagination}>
                  <button
                    type="button"
                    className={styles.pageButton}
                    disabled={!pageInfo?.hasPreviousPage || !pageInfo.startCursor}
                    onClick={() => fetchPage({ before: pageInfo?.startCursor })}
                    aria-label="Previous page"
                  >
                    <img src={PaginationPrevIcon} alt="" width={16} height={16} />
                  </button>
                  <button
                    type="button"
                    className={styles.pageButton}
                    disabled={!pageInfo?.hasNextPage || !pageInfo.endCursor}
                    onClick={() => fetchPage({ after: pageInfo?.endCursor })}
                    aria-label="Next page"
                  >
                    <img src={PaginationNextIcon} alt="" width={16} height={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <RewardDetailsDrawer
          challenge={drawerChallenge}
          defaultCurrency={defaultCurrency}
          onClose={() => setDrawerChallenge(null)}
          onClaimReward={onClaimReward}
        />
      </>
    );
  }
);

CompletedChallenges.displayName = 'CompletedChallenges';

export default CompletedChallenges;
